// De naam van een thema, en daarmee van elk inzicht dat eronder hangt.
//
// Eén bron voor de twee vormen die het scherm nodig heeft: `themeTitleParts`
// voor de weergave die de helften apart opmaakt (ThemeRow), `themeTitle` voor
// elke lijst die één regel tekst wil. Twee keer hetzelfde recept uitschrijven
// laat ze binnen een week uiteenlopen — en dan heet hetzelfde thema op twee
// tabbladen anders.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/**
 * @typedef {object} ThemeLike
 * @property {string} [effectName] - Naam van het effect
 * @property {string} [targetgroupLabel] - Leesbare naam van de doelgroep
 */

/**
 * De twee helften apart, allebei altijd een string.
 *
 * Een effect zonder naam wordt benoemd in plaats van leeggelaten: een lege
 * plek in een lijst leest als een weergavefout, terwijl het een echte toestand
 * is (de effectnaam komt uit een tenantkopie en kan ontbreken).
 *
 * @param {ThemeLike|null} [theme]
 * @returns {{effect: string, targetgroup: string}}
 */
export const themeTitleParts = (theme) => ({
    effect: (theme && typeof theme.effectName === 'string' && theme.effectName)
        ? theme.effectName
        : '(effect zonder naam)',
    targetgroup: (theme && typeof theme.targetgroupLabel === 'string' && theme.targetgroupLabel)
        ? theme.targetgroupLabel
        : '',
})

/**
 * Het thema als één regel: 'Zelfvertrouwen voor Jongeren'.
 *
 * Zonder doelgroep valt het woord 'voor' weg — 'Zelfvertrouwen voor' leest als
 * een afgebroken zin en suggereert dat er iets kwijt is.
 *
 * @param {ThemeLike|null} [theme]
 * @returns {string}
 */
export const themeTitle = (theme) => {
    const { effect, targetgroup } = themeTitleParts(theme)
    return targetgroup ? `${effect} voor ${targetgroup}` : effect
}

/**
 * De titel van één inzicht, gelezen uit het BEVROREN thema.
 *
 * Bewust de momentopname en niet een verse afleiding: de redactietekst is
 * geschreven bij dít thema, en het is dezelfde naam die de marketingsite toont.
 * Een vers afgeleide naam zou hier iets anders kunnen zeggen dan wat er buiten
 * staat.
 *
 * Drie toestanden blijven uit elkaar. 'Thema nog niet vastgelegd' hoort bij een
 * inzicht van vóór de momentopname — het thema bestáát, wij kennen het hier
 * alleen niet. Dat is iets anders dan een inzicht zonder thema-sleutel, en die
 * twee op één noemer zetten zou een lijst opleveren waarin niet te zien is
 * welke rij herstelbaar is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object|null} [insight] - een QuestionInsights-document
 * @returns {string}
 */
export const insightTitle = (insight) => {
    const frozen = insight && insight.Published && insight.Published.theme
    if (frozen) return themeTitle(frozen)
    if (insight && typeof insight.ThemeID === 'string' && insight.ThemeID) return 'Thema nog niet vastgelegd'
    return 'Zonder thema'
}

export default themeTitle
